/**
 * Tier II Facility Site Map – shared types.
 *
 * The map is stored as a GeoJSON FeatureCollection. Each feature carries a
 * `SiteMapFeatureProperties` object that describes what it represents in the
 * context of emergency-response site planning.
 *
 * Required paper submission elements (per EPCRA / first-responder standards):
 *   • Chemical storage locations (numbered, linked to inventory)
 *   • Pathways / access routes to those locations
 *   • Street intersections surrounding the facility (provided by OSM tiles)
 *   • Building outline with entrances, doorways, cylinders, tanks
 *   • Cardinal directions (N/S/E/W) – rendered on export
 *   • Map legend with symbol definitions
 *   • Evacuation routes
 */

/** Every feature drawn on the site map has one of these types. */
export type SiteMapFeatureType =
  | 'chemical-location' // Numbered pin – linked to a specific chemical in inventory
  | 'pathway' // Polyline – access route leading to a chemical storage area
  | 'evacuation-route' // Polyline – exit path for first responders / facility personnel
  | 'building-outline' // Polygon – outer footprint of a building or structure
  | 'room' // Polygon – interior room / area boundary
  | 'entrance' // Point – door or entrance (incl. emergency exits)
  | 'cylinder' // Point – compressed gas cylinder storage
  | 'tank' // Point – storage tank (above- or below-ground)
  | 'street-label' // Point – label for a nearby street / intersection
  | 'annotation'; // Point or Polygon – free-form text label / callout

/** Properties carried by every SiteMap GeoJSON feature. */
export type SiteMapStrokePattern = 'solid' | 'dashed' | 'dotted' | 'dash-dot';
export const SITE_MAP_STROKE_PATTERNS: Record<SiteMapStrokePattern, { label: string; dash: number[] }> = {
  solid: { label: 'Solid', dash: [] },
  dashed: { label: 'Dashed', dash: [3, 2] },
  dotted: { label: 'Dotted', dash: [1, 2] },
  'dash-dot': { label: 'Dash-dot', dash: [5, 2.5, 1, 2.5] },
};

export interface SiteMapFeatureProperties {
  featureType: SiteMapFeatureType;
  /** Human-readable label shown on the map and in the legend. */
  label?: string;
  /** Sequential number shown on the map pin (chemical-location only). */
  chemicalNumber?: number;
  /** Links this feature to a specific chemical (casOrCategory) in inventory. */
  casOrCategory?: string;
  /** Stroke / fill colour override (CSS hex string). */
  color?: string;
  /** Border/line pattern; omitted uses the feature category's default. */
  strokePattern?: SiteMapStrokePattern;
  /** Independently placed label center, in GeoJSON order [longitude, latitude]. */
  labelPosition?: [number, number];
  /** Shared facility storage location represented by this pin or area. */
  storageLocationID?: number;
  /** Extra notes printed in the legend. */
  note?: string;
  /** CapacityLine.id of the vessel this marker was placed from (for sidebar count). */
  vesselId?: string;
}

/** A single GeoJSON feature with strongly-typed properties. */
export interface SiteMapFeature {
  type: 'Feature';
  geometry:
    | { type: 'Point'; coordinates: [number, number] }
    | { type: 'LineString'; coordinates: [number, number][] }
    | { type: 'Polygon'; coordinates: [number, number][][] };
  properties: SiteMapFeatureProperties;
}

/** The full GeoJSON FeatureCollection stored for a facility/year site map. */
export interface SiteMapFeatureCollection {
  type: 'FeatureCollection';
  features: SiteMapFeature[];
}

/** The full site map record (mirrors the DB row). */
export interface TierIISiteMap {
  facilityID: number;
  year: number;
  /** Map centre latitude (WGS-84). */
  centerLat: number;
  /** Map centre longitude (WGS-84). */
  centerLng: number;
  /** Leaflet zoom level (typically 17–19 for a facility site). */
  zoom: number;
  features: SiteMapFeatureCollection;
  /** Firebase Storage path to an uploaded floor plan / blueprint image. */
  floorPlanStoragePath: string | null;
  /** [[south, west], [north, east]] lat/lng bounds for the floor plan overlay. */
  floorPlanBounds: [[number, number], [number, number]] | null;
  updatedAt: string | null;
}

/** Polygon fill opacity shared by drawing, saved map rendering, and PDF export. */
export const SITE_MAP_POLYGON_FILL_OPACITY = 0.15;

/** Default colours by feature type (used by editor and PDF renderer). */
export const SITE_MAP_COLORS: Record<SiteMapFeatureType, string> = {
  'chemical-location': '#dc2626', // red-600
  pathway: '#2563eb', // blue-600
  'evacuation-route': '#16a34a', // green-600
  'building-outline': '#374151', // gray-700
  room: '#6b7280', // gray-500
  entrance: '#d97706', // amber-600
  cylinder: '#7c3aed', // violet-600
  tank: '#0891b2', // cyan-600
  'street-label': '#6b7280', // gray-500
  annotation: '#1f2937', // gray-800
};

/** Human-readable labels for each feature type (used in legend). */
export const SITE_MAP_LABELS: Record<SiteMapFeatureType, string> = {
  'chemical-location': 'Chemical Storage Location',
  pathway: 'Access Pathway',
  'evacuation-route': 'Evacuation Route',
  'building-outline': 'Building Outline',
  room: 'Room / Area',
  entrance: 'Entrance / Doorway',
  cylinder: 'Cylinder Storage',
  tank: 'Storage Tank',
  'street-label': 'Street / Intersection Label',
  annotation: 'Annotation',
};

// ── Export / print layout configuration ───────────────────────────────────────

/** Page orientation for the exported PDF. */
export type SiteMapOrientation = 'portrait' | 'landscape';

/**
 * A physical storage vessel from the facility's capacity-based inventory,
 * used for drag-and-drop placement in the site map editor.
 */
export interface SiteMapVesselItem {
  /** Matches `CapacityLine.id`. */
  id: string;
  /** Vessel description (e.g. "55-gal drum"). */
  description: string;
  /** Product name the vessel belongs to. */
  productName: string;
  /** Site-map feature to place on drop. */
  featureType: 'cylinder' | 'tank';
}

/** Geographic rectangle (WGS-84 degrees) captured for the export. */
export interface SiteMapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * A single, user-arrangeable legend entry for the exported PDF.
 * `key` identifies what it represents:
 *   • `type:<featureType>` – a symbol swatch for a feature category.
 *   • `chem:<index>`       – a numbered chemical storage location.
 * The array order is the render order; `visible` toggles it on/off; `label`
 * is the (editable) display text.
 */
export interface SiteMapLegendItem {
  geometryType?: SiteMapFeature['geometry']['type'];
  key: string;
  label: string;
  visible: boolean;
  /** Draw as "N. label" for chemical entries; omitted for symbol swatches. */
  number?: number;
  /** Current feature color, used for swatches and numbered chemical symbols. */
  color?: string;
  featureType?: SiteMapFeatureType;
  strokePattern?: SiteMapStrokePattern;
  storageLocationID?: number;
  /** Generated label, so refreshing features can distinguish custom legend text. */
  sourceLabel?: string;
}

/**
 * A draggable/resizable layout box, expressed as a fraction (0–1) of the
 * page width/height, top-left origin (y increases downward — plain CSS
 * convention). Resolution- and orientation-independent: the same fractions
 * are re-applied against whichever PAGE_W/PAGE_H is current.
 */
export interface SiteMapElementRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * User-adjustable position/size/font-size overrides for the PDF's header,
 * map and legend blocks (Export Setup "resize/move" controls). Any omitted
 * field falls back to the computed default for the current orientation —
 * see `defaultSiteMapLayout()`.
 */
export interface SiteMapLayoutOverrides {
  header?: SiteMapElementRect;
  map?: SiteMapElementRect;
  legend?: SiteMapElementRect;
  titleFontSize?: number;
  legendFontSize?: number;
  /** Extra vertical gap (pt) added between legend rows, on top of the font-driven default. */
  legendRowSpacing?: number;
}

/** User-configurable options captured by the Export Setup dialog. */
export interface SiteMapExportConfig {
  orientation: SiteMapOrientation;
  /** Header title line (defaults to "TIER II FACILITY SITE MAP" when blank). */
  title: string;
  /** Ordered, editable legend entries. Empty ⇒ auto-generate from features. */
  legend: SiteMapLegendItem[];
  /**
   * Exact geographic rectangle to render (from the preview map). When present
   * the PDF renders this bbox into the map window for a WYSIWYG result; when
   * absent it falls back to the saved center/zoom.
   */
  bounds?: SiteMapBounds;
  /** Manual position/size/font-size overrides for the header/map/legend blocks. */
  layout?: SiteMapLayoutOverrides;
}

/** Default title shown in the PDF header. */
export const SITE_MAP_DEFAULT_TITLE = 'TIER II FACILITY SITE MAP';

/**
 * Page + map + legend geometry (PDF points) for a given orientation. Single
 * source of truth shared by the PDF renderer and the Export Setup preview so
 * the on-screen map area has exactly the same aspect ratio as the printed map.
 */
export function siteMapPageGeometry(orientation: SiteMapOrientation) {
  const portrait = orientation !== 'landscape';
  const PAGE_W = portrait ? 612 : 792; // 8.5" × 11" / 11" × 8.5"
  const PAGE_H = portrait ? 792 : 612;
  const MARGIN = 28;
  const HEADER_H = 72;
  const LEGEND_H = portrait ? 140 : 96;
  const MAP_X = MARGIN + 20; // extra room for W cardinal label
  const MAP_Y = MARGIN + HEADER_H + 8;
  const MAP_W = PAGE_W - MARGIN * 2 - 20; // leave room for E label
  const MAP_H = PAGE_H - MARGIN * 2 - HEADER_H - LEGEND_H - 16;
  // Fixed-width legend column grid — shared by the PDF renderer and the
  // Export Setup preview so entries line up in disciplined rows/columns
  // instead of wrapping raggedly at variable widths.
  const LEGEND_COL_W = 170;
  const LEGEND_COLS = Math.max(3, Math.floor((PAGE_W - MARGIN * 2) / LEGEND_COL_W));
  return { PAGE_W, PAGE_H, MARGIN, HEADER_H, LEGEND_H, MAP_X, MAP_Y, MAP_W, MAP_H, LEGEND_COL_W, LEGEND_COLS };
}

/**
 * Default (fraction-based) layout boxes + font sizes for a given orientation.
 * Single source of truth used both to seed the Export Setup dialog's
 * editable/draggable layout and as the PDF renderer's fallback for any block
 * the user hasn't manually repositioned. Derived from `siteMapPageGeometry`
 * so the un-customized defaults reproduce the original fixed layout exactly.
 *
 * `y` is top-down (distance from the page's top edge), matching CSS `top` —
 * NOT pdf-lib's bottom-up page coordinate. Convert with `layoutRectToPoints`
 * and, for pdf-lib drawing calls, `PAGE_H - r.y - r.h`.
 */
export function defaultSiteMapLayout(
  orientation: SiteMapOrientation,
): Required<
  Pick<SiteMapLayoutOverrides, 'header' | 'map' | 'legend' | 'titleFontSize' | 'legendFontSize' | 'legendRowSpacing'>
> {
  const g = siteMapPageGeometry(orientation);
  const toRect = (x: number, yTop: number, w: number, h: number): SiteMapElementRect => ({
    x: x / g.PAGE_W,
    y: yTop / g.PAGE_H,
    w: w / g.PAGE_W,
    h: h / g.PAGE_H,
  });
  return {
    header: toRect(g.MARGIN, g.MARGIN, g.PAGE_W - g.MARGIN * 2, g.HEADER_H),
    map: toRect(g.MAP_X, g.MAP_Y, g.MAP_W, g.MAP_H),
    legend: toRect(g.MARGIN, g.PAGE_H - g.MARGIN - g.LEGEND_H, g.PAGE_W - g.MARGIN * 2, g.LEGEND_H),
    titleFontSize: 14,
    legendFontSize: 7.5,
    legendRowSpacing: 0,
  };
}

/** Scale a fractional layout rect up to absolute points (still top-left origin). */
export function layoutRectToPoints(
  rect: SiteMapElementRect,
  pageW: number,
  pageH: number,
): { x: number; y: number; w: number; h: number } {
  return { x: rect.x * pageW, y: rect.y * pageH, w: rect.w * pageW, h: rect.h * pageH };
}

/**
 * Build the default legend entries from a feature collection: one swatch per
 * feature type present, followed by one numbered entry per chemical location.
 * Used by the editor to seed the Export Setup dialog and by the PDF renderer
 * as a fallback when no explicit legend is supplied.
 */
export function buildDefaultLegend(
  features: SiteMapFeature[],
  chemicalNames: (cas: string) => string | undefined,
): SiteMapLegendItem[] {
  const items: SiteMapLegendItem[] = [];

  // Generic type swatches — skip cylinder/tank (listed individually below)
  // and chemical-location (listed as numbered entries below).
  const unlinked = features.filter((f) => !f.properties.storageLocationID);
  const typesPresent = [...new Set(unlinked.map((f) => f.properties.featureType))];
  for (const ft of typesPresent) {
    if (ft === 'cylinder' || ft === 'tank' || ft === 'chemical-location') continue;
    const groups = new Map<string, SiteMapFeature[]>();
    for (const feature of unlinked.filter((f) => f.properties.featureType === ft)) {
      const color = siteMapFeatureColor(feature.properties);
      const key = `${color}:${siteMapStrokePattern(feature.properties)}`;
      groups.set(key, [...(groups.get(key) ?? []), feature]);
    }
    [...groups.values()].forEach((members, index) => {
      const color = siteMapFeatureColor(members[0]!.properties);
      const strokePattern = siteMapStrokePattern(members[0]!.properties);
      const names = members
        .map((f) => f.properties.label)
        .filter(Boolean)
        .join(', ');
      const label = groups.size > 1 ? `${SITE_MAP_LABELS[ft]} - ${names || `Group ${index + 1}`}` : SITE_MAP_LABELS[ft];
      items.push({
        key: index === 0 ? `type:${ft}` : `type:${ft}:${index}`,
        label,
        sourceLabel: label,
        visible: true,
        color,
        featureType: ft,
        strokePattern,
      });
    });
  }

  // Numbered chemical-location entries.
  for (const f of features.filter((f) => f.properties.storageLocationID)) {
    const p = f.properties;
    const label = p.label ?? 'Storage location';
    items.push({
      key: `location:${p.storageLocationID}`,
      label,
      sourceLabel: label,
      visible: true,
      color: siteMapFeatureColor(p),
      featureType: p.featureType,
      strokePattern: siteMapStrokePattern(p),
      storageLocationID: p.storageLocationID!,
      geometryType: f.geometry.type,
    });
  }
  const chemFeatures = unlinked.filter((f) => f.properties.featureType === 'chemical-location');
  chemFeatures.forEach((f, i) => {
    const num = f.properties.chemicalNumber ?? i + 1;
    const cas = f.properties.casOrCategory ?? '';
    const name = chemicalNames(cas) ?? f.properties.label ?? cas;
    items.push({
      key: `chem:${i}`,
      label: name,
      sourceLabel: name,
      visible: true,
      number: num,
      color: siteMapFeatureColor(f.properties),
      featureType: 'chemical-location',
    });
  });

  // Individual vessel entries: each placed cylinder/tank gets its own legend row
  // showing the vessel description and the chemical it stores (note field).
  const vesselFeatures = unlinked.filter(
    (f) => f.properties.featureType === 'cylinder' || f.properties.featureType === 'tank',
  );
  vesselFeatures.forEach((f, i) => {
    const desc = f.properties.label ?? SITE_MAP_LABELS[f.properties.featureType];
    const chemical = f.properties.note; // productName stored in note on drag-drop
    const displayLabel = chemical ? `${desc} – ${chemical}` : desc;
    const color = siteMapFeatureColor(f.properties);
    items.push({
      key: `vessel:${i}`,
      label: displayLabel,
      sourceLabel: displayLabel,
      visible: true,
      color,
      featureType: f.properties.featureType,
    });
  });

  return items;
}

export function siteMapFeatureColor(properties: SiteMapFeatureProperties): string {
  return properties.color && /^#[0-9a-f]{6}$/i.test(properties.color)
    ? properties.color.toLowerCase()
    : SITE_MAP_COLORS[properties.featureType];
}

export function siteMapStrokePattern(properties: SiteMapFeatureProperties): SiteMapStrokePattern {
  if (properties.strokePattern && Object.hasOwn(SITE_MAP_STROKE_PATTERNS, properties.strokePattern))
    return properties.strokePattern;
  if (properties.featureType === 'evacuation-route') return 'dash-dot';
  if (properties.featureType === 'pathway') return 'dashed';
  if (properties.featureType === 'annotation') return 'dotted';
  return 'solid';
}

/** Refresh feature-derived styling while retaining the user's order, visibility and edited labels. */
export function refreshSiteMapLegend(saved: readonly SiteMapLegendItem[], current: readonly SiteMapLegendItem[]) {
  const remaining = new Map(current.map((item) => [item.key, item]));
  const result: SiteMapLegendItem[] = [];
  for (const old of saved) {
    const fresh = remaining.get(old.key);
    if (!fresh) continue;
    result.push({
      ...fresh,
      visible: old.visible,
      label: fresh.storageLocationID
        ? fresh.label
        : old.sourceLabel == null || old.label !== old.sourceLabel
          ? old.label
          : fresh.label,
    });
    remaining.delete(old.key);
  }
  return [...result, ...remaining.values()];
}
